<template>
  <div class="grid grid-cols-4 gap-4 mb-4">
    <div class="col-span-2">
      <div class="space-y-2">
        <LabelComponent>Product Name</LabelComponent>
        <Input v-model="order.name" type="text" placeholder="Product Name" @input="validateProductName" />
        <span v-if="orderErrors.name !== ''" class="text-sm font-medium text-destructive">
          {{ orderErrors.name }}
        </span>
      </div>
    </div>
    <div class="col-span-2">
      <div class="space-y-2">
        <LabelComponent>Order Reference</LabelComponent>
        <Input v-model="order.reference" type="text" placeholder="Order Reference" @input="validateOrderReference" />
        <span v-if="orderErrors.reference !== ''" class="text-sm font-medium text-destructive">
          {{ orderErrors.reference }}
        </span>
      </div>
    </div>
    <div class="col-span-4">
      <div class="space-y-2">
        <LabelComponent>Total Order Price</LabelComponent>
        <div class="relative">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-grey-light-400 text-lg">R</span>
          <Input v-model="order.amountDue" type="text" class="pl-7" @input="validateAmountDue" placeholder="1,000" />
        </div>
      </div>
      <span v-if="orderErrors.amountDue !== ''" class="text-sm font-medium text-destructive">
        {{ orderErrors.amountDue }}
      </span>
    </div>
    <div class="col-span-4">
      <div class="space-y-2">
        <LabelComponent>Deposit Type</LabelComponent>
        <Select v-model="order.depositType" @update:modelValue="onDepositTypeChange">
          <SelectTrigger>
            <SelectValue class="font-medium" placeholder="Deposit Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="PERCENTAGE">Percentage</SelectItem>
              <SelectItem value="FLAT">Flat Fee</SelectItem>
              <SelectItem value="INSTALMENT">First Instalment</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
    <template v-if="order.depositType === 'PERCENTAGE'">
      <div class="col-span-1">
        <div class="space-y-2">
          <LabelComponent>Percentage</LabelComponent>
          <Select v-model="depositPercentage" @update:modelValue="onDepositPercentageChange">
            <SelectTrigger>
              <SelectValue placeholder="0%" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem v-for="i in 100" :key="i" :value="i.toString()">{{ `${i}%` }}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <span v-if="orderErrors.depositPerc !== ''" class="text-sm font-medium text-destructive">
          {{ orderErrors.depositPerc }}
        </span>
      </div>
      <div class="col-span-3">
        <div class="space-y-2">
          <LabelComponent>Deposit Amount</LabelComponent>
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-grey-light-400 text-lg"> R </span>
            <Input v-model="order.depositAmount" readonly disabled type="text" class="pl-7" placeholder="1,000.00" />
          </div>
        </div>
      </div>
      <div class="col-span-4 -mt-4" v-if="orderErrors.depositAmount !== ''">
        <span class="text-sm font-medium text-destructive">
          {{ orderErrors.depositAmount }}
        </span>
      </div>
    </template>
    <template v-else-if="order.depositType === 'FLAT'">
      <div class="col-span-4">
        <div class="space-y-2">
          <LabelComponent>Deposit Amount</LabelComponent>
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-grey-light-400 text-lg">R</span>
            <Input
              v-model="order.depositAmount"
              type="text"
              @input="validateDepositAmount"
              class="pl-7"
              placeholder="1,000.00"
            />
          </div>
        </div>
        <span v-if="orderErrors.depositAmount !== ''" class="text-sm font-medium text-destructive">
          {{ orderErrors.depositAmount }}
        </span>
      </div>
    </template>
    <div class="col-span-2">
      <Popover v-model:open="showDatePicker">
        <PopoverTrigger as-child>
          <div class="space-y-2">
            <LabelComponent>Select End Date</LabelComponent>
            <Button class="w-full" variant="outline">
              {{ formattedDate }}
            </Button>
          </div>
        </PopoverTrigger>

        <PopoverContent class="w-auto">
          <Calendar
            v-model="date"
            :minValue="minDate"
            :maxValue="maxDate"
            :weekday-format="'short'"
            class="rounded-md"
            @update:modelValue="onDateChange"
          />
        </PopoverContent>
      </Popover>
    </div>
    <div class="col-span-2" v-if="useTravelDate">
      <Popover v-model:open="showTravelDatePicker">
        <PopoverTrigger as-child>
          <div class="space-y-2">
            <LabelComponent>Select Travel Date</LabelComponent>
            <Button class="w-full" variant="outline">
              {{ travelDateFormatted }}
            </Button>
          </div>
        </PopoverTrigger>

        <PopoverContent class="w-auto">
          <Calendar
            v-model="travelDate"
            :minValue="minDate"
            :maxValue="maxDate"
            :weekday-format="'short'"
            class="rounded-md"
            @update:modelValue="onTravelDateChange"
          />
        </PopoverContent>
      </Popover>
    </div>
    <div class="col-span-2 flex items-center mt-7">
      <span v-if="orderErrors.endDate !== ''" class="text-sm font-medium text-destructive">
        {{ orderErrors.endDate }}
      </span>
    </div>
    <div class="col-span-2 flex items-center mt-7">
      <span v-if="orderErrors.travelDate !== ''" class="text-sm font-medium text-destructive">
        {{ orderErrors.travelDate }}
      </span>
    </div>
    <div class="col-span-4">
      <div class="flex gap-2 justify-between items-center">
        <LabelComponent>Change default order expiry</LabelComponent>
        <SwitchComponent
          v-model="changeDefaultOrderExpiry"
          @update:modelValue="(e) => emit('update:changeDefaultOrderExpiry', e)"
          class="!mt-0"
        />
      </div>
    </div>
    <template v-if="changeDefaultOrderExpiry">
      <div class="col-span-2">
        <Input v-model="expiryTime" type="text" @input="validateExpiryTime" placeholder="Enter Value" />
      </div>
      <div class="col-span-2">
        <Select v-model="expiryType" @update:modelValue="onExpiryTypeChange">
          <SelectTrigger>
            <SelectValue placeholder="Select the unit of time" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="minutes">Minutes</SelectItem>
              <SelectItem value="hours">Hours</SelectItem>
              <SelectItem value="days">Days</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div class="col-span-4 -mt-4" v-if="orderErrors.orderExpiryDuration !== ''">
        <span class="text-sm font-medium text-destructive">
          {{ orderErrors.orderExpiryDuration }}
        </span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import type { OrderErrors } from '@/models/orders';
import { isNumber, isSafeText } from '@/utils/form.utils';
import { displayDate } from '@/utils/format.utils';
import { getLocalTimeZone, parseDate, today, type DateValue } from '@internationalized/date';
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  order: Partial<ModelOrderResponse>;
  orderErrors: OrderErrors;
  changeDefaultOrderExpiry: boolean;
  timeValues: { expiryTime: string; expiryType: 'minutes' | 'hours' | 'days' };
  isAdmin: boolean;
  useTravelDate: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:date', value: string): void;
  (e: 'update:travelDate', value: string): void;
  (e: 'update:changeDefaultOrderExpiry', value: boolean): void;
}>();

const showDatePicker = ref(false);
const showTravelDatePicker = ref(false);

const depositPercentage = ref(props.order.depositPerc?.toString() || '');
const changeDefaultOrderExpiry = ref(props.changeDefaultOrderExpiry);
const expiryTime = ref(props.timeValues.expiryTime);
const expiryType = ref<'minutes' | 'hours' | 'days'>(props.timeValues.expiryType);

const date = ref(
  props.order.endDateMin ? parseDate(props.order.endDateMin.replace(/\//g, '-')) : today(getLocalTimeZone()),
) as Ref<DateValue>;

const travelDate = ref(
  props.order.endDateMin ? parseDate(props.order.endDateMin.replace(/\//g, '-')) : today(getLocalTimeZone()),
) as Ref<DateValue>;

const minDate = computed(() => (props.order.endDateMin ? parseDate(props.order.endDateMin) : undefined));
const maxDate = computed(() => (props.order.endDateMax ? parseDate(props.order.endDateMax) : undefined));
const formattedDate = computed(() => (date.value ? displayDate(new Date(date.value.toString())) : 'Select End Date'));
const travelDateFormatted = computed(() =>
  travelDate.value ? displayDate(new Date(travelDate.value.toString())) : 'Select Travel Date',
);

const validateProductName = () => {
  const value = props.order.name || '';
  if (!value || value.trim() === '') {
    props.orderErrors.name = 'Please enter a product name';
  } else if (!isSafeText(value)) {
    props.orderErrors.name = 'Product name contains invalid characters';
  } else {
    props.orderErrors.name = '';
  }
};

const validateOrderReference = () => {
  const value = props.order.reference || '';
  if (!value || value.trim() === '') {
    props.orderErrors.reference = 'Please enter a reference number';
  } else if (!isSafeText(value)) {
    props.orderErrors.reference = 'Reference number contains invalid characters';
  } else {
    props.orderErrors.reference = '';
  }
};

const validateAmountDue = (event: Event) => {
  const inputValue = isNumber(event);
  props.order.amountDue = Number(inputValue);

  const amountDue = Number(inputValue);
  if (isNaN(amountDue) || amountDue <= 0) {
    props.orderErrors.amountDue = 'Please enter a total order value';
  } else {
    props.orderErrors.amountDue = '';
  }

  if (props.order.depositType === 'FLAT') {
    validateDepositAmountValue();
  }
};

const validateDepositAmountValue = () => {
  const depositAmount = Number(props.order.depositAmount || 0);
  const orderAmount = Number(props.order.amountDue || 0);

  if (props.order.depositType === 'FLAT') {
    if (isNaN(depositAmount) || depositAmount <= 0) {
      props.orderErrors.depositAmount = 'Amount must be more than 0';
    } else if (depositAmount > orderAmount) {
      props.orderErrors.depositAmount = 'Deposit amount can not be greater than order amount';
    } else {
      props.orderErrors.depositAmount = '';
    }
  } else if (props.order.depositType === 'PERCENTAGE') {
    if (isNaN(depositAmount) || depositAmount <= 0) {
      props.orderErrors.depositAmount = 'Amount must be more than 0';
    } else {
      props.orderErrors.depositAmount = '';
    }
  }
};

const validateDepositPercentage = () => {
  const percentage = props.order.depositPerc || 0;
  if (props.order.depositType === 'PERCENTAGE') {
    if (percentage <= 0) {
      props.orderErrors.depositPerc = 'Required';
    } else {
      props.orderErrors.depositPerc = '';
    }
  }
};

const validateExpiryTime = (event: Event) => {
  const inputValue = isNumber(event);
  expiryTime.value = inputValue;

  if (props.changeDefaultOrderExpiry) {
    if (!inputValue || Number(inputValue) <= 0) {
      props.orderErrors.orderExpiryDuration = 'Duration must be more than 0';
    } else {
      props.orderErrors.orderExpiryDuration = '';
    }
  }
};

const validateDepositAmount = (event: Event) => {
  const inputValue = isNumber(event);
  props.order.depositAmount = Number(inputValue);
  validateDepositAmountValue();
};

// Event handlers for selects and other components
const onDepositTypeChange = () => {
  // Clear deposit-related errors when type changes
  props.orderErrors.depositPerc = '';
  props.orderErrors.depositAmount = '';

  // Recalculate deposit amount if switching to percentage
  if (props.order.depositType === 'PERCENTAGE') {
    const amountDue = Number(props.order.amountDue || 0);
    const percentage = Number(props.order.depositPerc || 0);

    if (amountDue > 0 && percentage > 0) {
      props.order.depositAmount = (amountDue * percentage) / 100;
    } else {
      props.order.depositAmount = 0;
    }
  }
};

const onDepositPercentageChange = (newVal: string) => {
  props.order.depositPerc = newVal ? Number(newVal) : 0;
  validateDepositPercentage();
};

const onDateChange = () => {
  if (date.value) {
    emit('update:date', date.value.toString());
    props.orderErrors.endDate = '';
  }
};

const onTravelDateChange = () => {
  if (travelDate.value) {
    emit('update:travelDate', travelDate.value.toString());
    props.orderErrors.travelDate = '';
  }
};
const onExpiryTypeChange = () => {
  // Re-validate expiry time when type changes
  if (expiryTime.value) {
    validateExpiryTime({ target: { value: expiryTime.value } } as any);
  }
};

onMounted(() => {
  // Converting values from cents to rands
  if (props.order.amountDue && props.order.amountDue > 100) {
    props.order.amountDue = props.order.amountDue / 100;
  } else {
    props.order.amountDue = 0;
  }

  if (props.order.depositAmount && props.order.depositAmount > 100) {
    props.order.depositAmount = props.order.depositAmount / 100;
  } else {
    props.order.depositAmount = 0;
  }
});

watch(depositPercentage, (newVal) => (props.order.depositPerc = newVal ? Number(newVal) : 0));

watch(
  date,
  (newVal) => {
    if (newVal) emit('update:date', newVal?.toString());
    showDatePicker.value = false;
  },
  { immediate: true },
);

watch(
  travelDate,
  (newVal) => {
    if (newVal) emit('update:travelDate', newVal?.toString());
    showTravelDatePicker.value = false;
  },
  { immediate: true },
);

watch([() => expiryType.value, () => expiryTime.value], ([newType, newTime]) => {
  if (newType && newTime) {
    const timeValue = Number(newTime);

    if (newType === 'minutes') {
      if (timeValue >= 0 && timeValue <= 86400) {
        props.order.expire = newTime;
      } else {
        props.orderErrors.orderExpiryDuration = `Expiry can't be more than 86400 minutes into the future.`;
      }
    }

    if (newType === 'hours') {
      if (timeValue >= 0 && timeValue <= 1440) {
        props.order.expire = (timeValue * 60).toString();
      } else {
        props.orderErrors.orderExpiryDuration = `Expiry can't be more than 1440 (60 days)`;
      }
    }

    if (newType === 'days') {
      if (timeValue >= 0 && timeValue <= 60) {
        props.order.expire = (timeValue * 24 * 60).toString();
      } else {
        props.orderErrors.orderExpiryDuration = `Expiry can't be more than 60`;
      }
    }
  }
});
</script>
